import { css } from '@emotion/react'
import { Cell, FormControl, Grid, HFlow, Icon, Text, Theme, Tooltip, useTheme } from 'bold-ui'
import { RichTextField } from 'components/form/core/RichTextField/RichTextField'
import { Cbo } from 'graphql/types.generated'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'
import { LotacoesCuidadoCompartilhadoSelectField } from 'view/atendimentos/detail/soap/plano/cuidado-compartilhado/components/LotacoesCuidadoCompartilhadoSelectField'
import {
  CondutaGroupEnum,
  CUIDADO_COMPARTILHADO_CONDUTAS_PLACEHOLDER,
  CuidadoCompartilhadoLotacaoSolicitanteModel,
  DiscussaoCasoExecutanteFieldsModel,
  DiscussaoCasoSolicitanteFieldsModel,
  TrocaExecutanteCuidadoCompartilhadoFieldsModel,
} from 'view/cuidados-compartilhados/model-cuidadoCompartilhado'

import { OnCondutaGroupChangeCallback } from '../../discussao-caso/DiscussaoCasoFieldGroup'
import { CboTrocaExecutanteSelectField } from '../CboTrocaExecutanteSelectField/CboTrocaExecutanteSelectField'
import { CondutaGroupCuidadoCompartilhadoRadioFieldContainer } from './CondutaGroupCuidadoCompartilhadoRadioFieldContainer'

interface TrocaExecutanteCuidadoCompartilhadoFieldProps {
  path: MetaPath<DiscussaoCasoExecutanteFieldsModel | DiscussaoCasoSolicitanteFieldsModel>
  lotacaoSolicitante: CuidadoCompartilhadoLotacaoSolicitanteModel
  profissionalExecutanteId: ID
  equipeExecutanteId: ID
  unidadeSaudeExecutanteId: ID
  isEspecializada: boolean
  onCondutaGroupChange: OnCondutaGroupChangeCallback
}

export const TrocaExecutanteCuidadoCompartilhadoField = (props: TrocaExecutanteCuidadoCompartilhadoFieldProps) => {
  const {
    path,
    lotacaoSolicitante,
    profissionalExecutanteId,
    equipeExecutanteId,
    unidadeSaudeExecutanteId,
    isEspecializada,
    onCondutaGroupChange,
  } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const name = path[CondutaGroupEnum.TROCA_PROFISSIONAL_REFERENCIA]
  const excludeProfissionaisIds = [lotacaoSolicitante.profissional.id, profissionalExecutanteId]
  const equipeSolicitanteId = !isEspecializada ? lotacaoSolicitante.equipe?.id : null
  const condutaText = isEspecializada
    ? 'A troca de profissional de referência pode ser feita para qualquer profissional deste estabelecimento.'
    : 'A troca de profissional de referência pode ser feita para qualquer profissional vinculado com a equipe do profissional solicitante.'
  const tooltipText = isEspecializada
    ? 'Este campo exibe apenas os profissionais deste estabelecimento.'
    : 'O campo só traz profissionais de equipes NASF e eMulti vinculados a essa unidade.'

  const {
    input: { value: cbo },
  } = useField<Cbo>(name.cbo.absolutePath(), { subscription: { value: true } })

  return (
    <CondutaGroupCuidadoCompartilhadoRadioFieldContainer<TrocaExecutanteCuidadoCompartilhadoFieldsModel>
      name={name}
      pathCondutaGroup={path.condutaGroup}
      value={CondutaGroupEnum.TROCA_PROFISSIONAL_REFERENCIA}
      onAskUserConfirmation={onCondutaGroupChange}
    >
      <Grid>
        <Cell size={12}>
          <Text>{condutaText}</Text>
        </Cell>
        <Cell size={6}>
          <CboTrocaExecutanteSelectField
            name={name.cbo}
            label='CBO'
            estabelecimentoReferenciaId={unidadeSaudeExecutanteId}
            equipeSolicitanteId={equipeSolicitanteId}
            equipeExecutanteId={equipeExecutanteId}
            excludeProfissionaisIds={excludeProfissionaisIds}
            onlyVinculacaoEquipe={!isEspecializada}
          />
        </Cell>
        <Cell size={6}>
          <FormControl label='Profissional' required>
            <HFlow hSpacing={0.5} alignItems='baseline' style={styles.hFlow}>
              <LotacoesCuidadoCompartilhadoSelectField
                name={name.lotacao}
                unidadeSaudeId={unidadeSaudeExecutanteId}
                cboId={cbo?.id}
                excludeProfissionaisIds={excludeProfissionaisIds}
                equipeSolicitanteId={equipeSolicitanteId}
                equipeExecutanteId={equipeExecutanteId}
                isTrocaExecutante
                required
              />
              <Tooltip text={tooltipText} placement='top'>
                <Icon icon='infoCircleFilled' fill='normal' size={1} style={styles.iconTooltip} />
              </Tooltip>
            </HFlow>
          </FormControl>
        </Cell>
        <Cell size={12}>
          <RichTextField
            name={name.motivo}
            label='Motivo'
            placeholder={CUIDADO_COMPARTILHADO_CONDUTAS_PLACEHOLDER}
            maxLength={1000}
            required
          />
        </Cell>
      </Grid>
    </CondutaGroupCuidadoCompartilhadoRadioFieldContainer>
  )
}

const createStyles = (theme: Theme) => ({
  hFlow: css`
    grid-auto-columns: 1fr auto;
  `,
  iconTooltip: css`
    &:hover {
      fill: ${theme.pallete.primary.main};
    }
  `,
})
